import { TOOLTIP_PORTAL_NAME } from 'layouts/TooltipPortalTarget';
import { Component, Prop } from 'vue-property-decorator';
import { Component as TsxComponent } from 'vue-tsx-support';
import styles from './base-tooltip.css';

const SHOW_DELAY = 250;

export enum TooltipType {
  TOOLTIP = 'tooltip',
  POPOVER = 'popover',
}
export interface BaseTooltipProps {
  type?: TooltipType;
  text?: string | JSX.Element | Vue;
}
@Component
export default class BaseTooltip extends TsxComponent<BaseTooltipProps> {
  public $refs: {
    tooltipped: HTMLDivElement;
  };

  protected isPortalDisabled = true;

  protected isShown = false;

  protected isBeyondViewportTop = false;

  protected isBeyondViewportLeft = false;

  protected isBeyondViewportRight = false;

  protected style: Partial<CSSStyleDeclaration> = {};

  protected textAlignment = 'left';

  protected timeoutId = Number.NaN;

  @Prop({ default: TooltipType.TOOLTIP })
  public type: NonNullable<BaseTooltipProps['type']>;

  @Prop()
  public text: BaseTooltipProps['text'];

  protected cancelTimeoutIfPresent() {
    if (this.timeoutId) {
      window.clearTimeout(this.timeoutId);
      this.timeoutId = Number.NaN;
    }
  }

  protected onMouseEnter() {
    this.cancelTimeoutIfPresent();

    this.timeoutId = window.setTimeout(() => {
      this.isPortalDisabled = false;
      this.isShown = true;
      this.timeoutId = Number.NaN;
    }, SHOW_DELAY);
  }

  protected onMouseLeave() {
    this.cancelTimeoutIfPresent();

    this.isShown = false;

    this.isBeyondViewportTop = false;
    this.isBeyondViewportLeft = false;
    this.isBeyondViewportRight = false;

    // FAQ: need to reset, otherwise the flip detection below in onEnter will not
    // work the next time it is called
    this.style = {};
  }

  protected onEnter(tooltip: HTMLSpanElement, done: Function) {
    // rect of the tooltip element
    const rectTooltip = tooltip.getBoundingClientRect();
    // rect of element tooltip belongs to
    const rectTooltipped = this.$refs.tooltipped.getBoundingClientRect();

    /* left is center of tooltipped element(transform is applied in css) */
    const left = rectTooltipped.left + rectTooltipped.width / 2;

    this.isBeyondViewportTop = rectTooltipped.top - rectTooltip.height < 0;

    this.isBeyondViewportLeft = left - rectTooltip.width / 2 < 0;

    this.isBeyondViewportRight = left + rectTooltip.width / 2 > window.innerWidth;

    const top = this.isBeyondViewportTop
      ? rectTooltipped.top + rectTooltipped.height + rectTooltip.height
      : rectTooltipped.top;

    this.style = {
      left: `${rectTooltipped.left + rectTooltipped.width / 2}px`,
      top: `${top}px`,
      textAlign: this.textAlignment,
    };

    done();
  }

  protected onAfterLeave() {
    this.isPortalDisabled = true;
  }

  public render() {
    return (
      <div
        class={styles.baseTooltip}
        onMouseenter={this.onMouseEnter}
        onMouseleave={this.onMouseLeave}
        ref="tooltipped"
      >
        {this.$slots.default}

        <portal to={TOOLTIP_PORTAL_NAME} disabled={this.isPortalDisabled}>
          <transition
            appear={true}
            enter-class={styles.transitionSlideEnter}
            enter-to-class={styles.transitionSlideEnterTo}
            leave-class={styles.transitionSlideLeave}
            leave-to-class={styles.transitionSlideLeaveTo}
            onEnter={this.onEnter}
            onAfterLeave={this.onAfterLeave}
          >
            {this.isShown && (
              <span
                class={{
                  [styles.baseTooltip]: true,
                  [styles.baseTooltipPopover]: this.type === TooltipType.POPOVER,
                  [styles.baseTooltipText]: true,
                  [styles.baseTooltipTextBottom]: this.isBeyondViewportTop,
                  [styles.baseTooltipTextRight]: this.isBeyondViewportLeft,
                  [styles.baseTooltipTextLeft]: this.isBeyondViewportRight,
                }}
                domPropsInnerHTML={typeof this.text === 'string' ? this.text : undefined}
                style={this.style}
              >
                {
                  typeof this.text !== 'string' && this.text
                }
              </span>
            )}
          </transition>
        </portal>
      </div>
    );
  }
}
